/**
 * Human copy for the support desk's API enum tokens.
 *
 * The dashboard used to print `PENDING_CUSTOMER`, `TAX_COMPLIANCE` and `STATUS_CHANGED` raw in
 * both locales, on the argument that an operator cross-checks them against the database. That
 * held while the ops tool was English-only; it does not hold for a German agent reading a
 * dropdown of nine SCREAMING_SNAKE tokens to decide where a ticket belongs. Every token the
 * support screens SHOW is translated here — the tokens the app SENDS are untouched, because the
 * narrowing helpers in `support-api.ts` still work on the raw value and the `<option>` value
 * attribute is never the label.
 *
 * A JSX-free module, mirroring `support-context-fields.ts`, so `locale-parity.spec.ts` can check
 * the member lists and the catalogue against each other. That spec is the only thing standing
 * between a new enum member on the API and a dropdown entry that reads `NEEDS_TRIAGE`.
 */

import {
  ListSupportAdminTicketsCategoryEnum,
  ListSupportAdminTicketsPriorityEnum,
  ListSupportAdminTicketsStatusEnum,
  ListSupportAdminTicketsTierEnum,
} from '../../../../libs/api-client/src/generated/api/support-admin-api';
import {
  SubscriptionResponseDtoStatusEnum,
  SupportAdminMessageDtoAuthorTypeEnum,
  TaxScheme,
} from '../../../../libs/api-client/src/generated/models';
/**
 * Ticket history event types.
 *
 * The one list here that is HAND-WRITTEN: `SupportAdminTicketDetailDtoEventsInner.type` is typed
 * `string` by the generator, so there is no enum to read them off. Kept in the order the Prisma
 * enum declares them (`SupportTicketEventType`), and an unknown type falls back to its raw token
 * rather than to a dotted path — see {@link supportEnumLabelKey}.
 */
export const SUPPORT_EVENT_TYPES = [
  'CREATED',
  'STATUS_CHANGED',
  'CATEGORY_CHANGED',
  'PRIORITY_CHANGED',
  'ASSIGNED',
  'UNASSIGNED',
  'CUSTOMER_REPLIED',
  'AGENT_REPLIED',
  'INTERNAL_NOTE_ADDED',
  'REOPENED',
  // The review invitation's four. None carries a `fromValue`, and `toValue` is not a ticket
  // field: it is an ISO due date on SCHEDULED (localised as a date) and a reason token on
  // CANCELLED / SKIPPED (translated out of `reviewAskReason` below).
  'REVIEW_ASK_SCHEDULED',
  'REVIEW_ASK_SENT',
  'REVIEW_ASK_CANCELLED',
  'REVIEW_ASK_SKIPPED',
] as const;

/**
 * Every enum the support screens render, and its members.
 *
 * Read off the generated client wherever the generator emits one, for the same reason the pages
 * build their dropdowns that way: a hand-typed list can only ever disagree with the API.
 */
export const SUPPORT_ENUM_VALUES = {
  status: Object.values(ListSupportAdminTicketsStatusEnum),
  category: Object.values(ListSupportAdminTicketsCategoryEnum),
  priority: Object.values(ListSupportAdminTicketsPriorityEnum),
  tier: Object.values(ListSupportAdminTicketsTierEnum),
  event: SUPPORT_EVENT_TYPES,
  author: Object.values(SupportAdminMessageDtoAuthorTypeEnum),
  // Not ticket fields — these two are values INSIDE the frozen shop snapshot, which the sidebar
  // printed raw next to translated labels ("Steuerregelung: KLEINUNTERNEHMEN").
  subscriptionStatus: Object.values(SubscriptionResponseDtoStatusEnum),
  taxScheme: Object.values(TaxScheme),
  // The `toValue` tokens a review-invitation history entry carries. HAND-WRITTEN, for the same
  // reason `SUPPORT_EVENT_TYPES` is: the authoritative set lives in `libs/types`
  // (`REVIEW_ASK_SKIP_REASONS` + `REVIEW_ASK_CANCEL_REASONS`), and this app cannot import it —
  // its tsconfig deliberately carries no `@easy-invoices/*` paths, which is why the generated
  // client above comes in by relative path. `review-ask-gate.spec.ts` fails when that set
  // changes and names this file. Drift degrades rather than breaks: an unlabelled token prints
  // as its raw slug, exactly as an unknown status does.
  reviewAskReason: [
    // REVIEW_ASK_SKIP_REASONS — the gate's seven verdicts, plus the delivery failure.
    'ticket-not-resolved',
    'not-requested',
    'opted-out',
    'backed-off',
    'asked-recently',
    'shop-too-new',
    'no-support-email',
    'send-failed',
    // REVIEW_ASK_CANCEL_REASONS — why a pending invitation was withdrawn.
    'customer-replied',
    'reopened-by-merchant',
    'reopened-open',
  ],
} as const satisfies Record<string, readonly string[]>;

/** One of the enums {@link SUPPORT_ENUM_VALUES} carries copy for. */
export type SupportEnumGroup = keyof typeof SUPPORT_ENUM_VALUES;

/**
 * Catalogue key for an enum member, or `null` when there is no copy for it.
 *
 * `null` rather than the dotted path, exactly as `supportContextLabelKey` does it: a member the
 * API grows before this catalogue does must print as its RAW token, because an operator can act
 * on `NEEDS_TRIAGE` and can do nothing at all with `support.enum.status.NEEDS_TRIAGE`.
 */
export function supportEnumLabelKey(
  group: SupportEnumGroup,
  value: string,
): string | null {
  const members: readonly string[] = SUPPORT_ENUM_VALUES[group];
  return members.includes(value) ? `support.enum.${group}.${value}` : null;
}

/** The subset of the locale context this module needs — a translator, nothing else. */
export type Translate = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

/**
 * The label for an enum member, falling back to the raw token.
 *
 * @param t - Translator from `useLocale`, passed in rather than hooked so this stays a pure
 *   function callable from a `map` inside any component.
 */
export function supportEnumLabel(
  t: Translate,
  group: SupportEnumGroup,
  value: string | null | undefined,
): string {
  if (!value) return '';
  const key = supportEnumLabelKey(group, value);
  return key ? t(key) : value;
}

/**
 * Which enum a history event's `fromValue` / `toValue` are drawn from.
 *
 * `ASSIGNED` and `UNASSIGNED` carry a USERNAME rather than an enum member, and `CREATED` carries
 * nothing — both answer `null`, so the value prints as it stands. Without this the history panel
 * would show "STATUS_CHANGED — OPEN → RESOLVED" with the two sides untranslated, which is the
 * half-done version of this that reads as a bug rather than as a decision.
 */
export function supportEventValueGroup(
  eventType: string | undefined,
): SupportEnumGroup | null {
  switch (eventType) {
    case 'STATUS_CHANGED':
    case 'REOPENED':
      return 'status';
    case 'CATEGORY_CHANGED':
      return 'category';
    case 'PRIORITY_CHANGED':
      return 'priority';
    // Not a transition: `toValue` is why an invitation was withdrawn or produced no mail. It
    // reaches the same `sideLabel` path as any other value, which is the whole point — one
    // translator for every token these screens show. `REVIEW_ASK_SCHEDULED` is NOT here: its
    // `toValue` is an ISO due date, which `historyDetail` localises as a date instead.
    case 'REVIEW_ASK_CANCELLED':
    case 'REVIEW_ASK_SKIPPED':
      return 'reviewAskReason';
    default:
      return null;
  }
}

/**
 * Which enum a context-snapshot field's VALUE is drawn from, or `null` for a plain one.
 *
 * The snapshot is a `Json` column and arrives as `Record<string, unknown>`, so nothing about a
 * value's type survives the round trip — this is the only place that knows `taxScheme` holds an
 * enum member and `invoiceCount` holds a number.
 */
export function supportContextValueGroup(
  field: string,
): SupportEnumGroup | null {
  switch (field) {
    case 'tier':
      return 'tier';
    case 'subscriptionStatus':
      return 'subscriptionStatus';
    case 'taxScheme':
      return 'taxScheme';
    default:
      return null;
  }
}
